/**
 * Presentational helpers for the unlock transaction panel.
 * Display-only — does not construct or mutate transactions.
 * Shows imported inputs + user outputs only (fee funding is hidden).
 */

var _require = require('./unlock_tx_edit'),
  escapeHtml = _require.escapeHtml,
  formatNolanAmount = _require.formatNolanAmount,
  remainingSaitoNolan = _require.remainingSaitoNolan,
  remainingNftUnits = _require.remainingNftUnits,
  unlockBaseOutputs = _require.unlockBaseOutputs,
  isNftSlip = _require.isNftSlip;
var _require2 = require('./unlock_tx_fee'),
  hasUnlockFee = _require2.hasUnlockFee,
  canChangeUnlockFee = _require2.canChangeUnlockFee,
  isDefaultZeroUnlockFee = _require2.isDefaultZeroUnlockFee;
function controllingPublicKey(ctx) {
  var _ctx$lockedNftSlips, _ctx$lockedSlip, _ctx$lockedSlip2;
  if (!ctx) {
    return '';
  }
  if (ctx.p2shAddress) {
    return String(ctx.p2shAddress);
  }
  if (ctx.assetType === 'nft' && (_ctx$lockedNftSlips = ctx.lockedNftSlips) !== null && _ctx$lockedNftSlips !== void 0 && _ctx$lockedNftSlips[1]) {
    return String(ctx.lockedNftSlips[1].publicKey || ctx.lockedNftSlips[1].public_key || '');
  }
  return String(((_ctx$lockedSlip = ctx.lockedSlip) === null || _ctx$lockedSlip === void 0 ? void 0 : _ctx$lockedSlip.publicKey) || ((_ctx$lockedSlip2 = ctx.lockedSlip) === null || _ctx$lockedSlip2 === void 0 ? void 0 : _ctx$lockedSlip2.public_key) || '');
}

/**
 * Locked asset inputs from unlockContext only (no wallet fee inputs).
 */
function unlockInputRows(app, mod) {
  var ctx = mod === null || mod === void 0 ? void 0 : mod.unlockContext;
  var rows = [];
  if (ctx) {
    var pubkey = controllingPublicKey(ctx);
    if (ctx.assetType === 'nft') {
      var remaining = remainingNftUnits(mod);
      var locked = function () {
        try {
          var _ctx$lockedNftSlips2;
          return BigInt(ctx.nftAmount || ((_ctx$lockedNftSlips2 = ctx.lockedNftSlips) !== null && _ctx$lockedNftSlips2 !== void 0 && _ctx$lockedNftSlips2[1] ? String(ctx.lockedNftSlips[1].amount || 1) : '1'));
        } catch (_err) {
          return BigInt(1);
        }
      }();
      var spent = remaining < locked;
      var unitsText = "".concat(remaining.toString(), " unit").concat(remaining === BigInt(1) ? '' : 's');
      rows.push({
        kind: 'nft',
        role: 'locked',
        title: 'NFT',
        meta: pubkey,
        value: spent ? "".concat(escapeHtml(unitsText), " remaining") : escapeHtml(unitsText),
        selectable: true
      });
    } else {
      var _remaining = remainingSaitoNolan(mod);
      var _locked = function () {
        try {
          var _ctx$lockedSlip3;
          return BigInt(((_ctx$lockedSlip3 = ctx.lockedSlip) === null || _ctx$lockedSlip3 === void 0 ? void 0 : _ctx$lockedSlip3.amount) || 0);
        } catch (_err) {
          return BigInt(0);
        }
      }();
      var saito = formatNolanAmount(app, _remaining);
      var _spent = _remaining < _locked;
      rows.push({
        kind: 'saito',
        role: 'locked',
        title: 'SAITO',
        meta: pubkey,
        value: _spent ? "".concat(escapeHtml(saito), " SAITO remaining") : "".concat(escapeHtml(saito), " SAITO"),
        selectable: true
      });
    }
  }
  return rows;
}

/**
 * User outputs from unlock_transaction_base only.
 */
function unlockOutputRows(app, mod) {
  return unlockBaseOutputs(mod).map(function (slip, index) {
    var kind = isNftSlip(slip) ? 'nft' : 'saito';
    var title = kind === 'nft' ? 'NFT' : 'SAITO';
    var destination = String((slip === null || slip === void 0 ? void 0 : slip.publicKey) || (slip === null || slip === void 0 ? void 0 : slip.public_key) || '');
    var value = '';
    try {
      var amount = BigInt((slip === null || slip === void 0 ? void 0 : slip.amount) || 0);
      if (kind === 'saito') {
        value = "".concat(formatNolanAmount(app, amount), " SAITO");
      } else {
        value = "".concat(amount.toString(), " unit").concat(amount === BigInt(1) ? '' : 's');
      }
    } catch (_err) {
      value = String((slip === null || slip === void 0 ? void 0 : slip.amount) || '');
    }
    return {
      kind: kind,
      role: 'user',
      title: title,
      meta: destination,
      value: escapeHtml(value),
      index: index,
      deletable: true
    };
  });
}
function inputRowMarkup(row, index) {
  var selectedIndex = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var selectable = row.selectable !== false;
  var selected = selectable && selectedIndex != null && Number(selectedIndex) === index;
  var meta = row.meta ? "<div class=\"rs-tx-asset-meta\" title=\"".concat(escapeHtml(row.meta), "\">").concat(escapeHtml(row.meta), "</div>") : '';
  return "\n    <article\n      class=\"rs-tx-asset rs-tx-input".concat(selected ? ' is-selected' : '', "\"\n      data-kind=\"").concat(escapeHtml(row.kind || ''), "\"\n      data-role=\"").concat(escapeHtml(row.role || 'locked'), "\"\n      data-input-index=\"").concat(index, "\"\n      tabindex=\"0\"\n      role=\"button\"\n      aria-pressed=\"").concat(selected ? 'true' : 'false', "\"\n    >\n      <div class=\"rs-tx-asset-main\">\n        <div class=\"rs-tx-asset-row\">\n          <span class=\"rs-tx-asset-title\">").concat(escapeHtml(row.title), "</span>\n          <span class=\"rs-tx-asset-value\">").concat(row.value || '', "</span>\n        </div>\n        ").concat(meta, "\n      </div>\n    </article>");
}
function outputRowMarkup(row) {
  var meta = row.meta ? "<div class=\"rs-tx-asset-meta\" title=\"".concat(escapeHtml(row.meta), "\">").concat(escapeHtml(row.meta), "</div>") : "<div class=\"rs-tx-asset-meta\">Destination pending</div>";
  var deleteAttrs = row.deletable ? "tabindex=\"0\" role=\"button\" aria-label=\"Delete output\" data-output-index=\"".concat(row.index, "\"") : "data-output-index=\"".concat(row.index, "\"");
  return "\n    <article\n      class=\"rs-tx-asset rs-tx-output\"\n      data-kind=\"".concat(escapeHtml(row.kind || ''), "\"\n      data-role=\"").concat(escapeHtml(row.role || 'user'), "\"\n      ").concat(deleteAttrs, "\n    >\n      <div class=\"rs-tx-asset-main\">\n        <div class=\"rs-tx-asset-row\">\n          <span class=\"rs-tx-asset-title\">").concat(escapeHtml(row.title), "</span>\n          <span class=\"rs-tx-asset-value\">").concat(row.value || '', "</span>\n        </div>\n        ").concat(meta, "\n      </div>\n    </article>");
}
function feeActionMarkup(mod) {
  if (hasUnlockFee(mod) && !canChangeUnlockFee(mod)) {
    var label = String(mod.unlock_fee.feeSaito || '').trim() || '0';
    return "<span class=\"rs-tx-fee-status\" title=\"Fee is locked for this unlock\">Fee: ".concat(escapeHtml(label), " SAITO <span class=\"rs-tx-fee-check\" aria-hidden=\"true\">\u2713</span></span>");
  }
  if (isDefaultZeroUnlockFee(mod)) {
    return "<span class=\"rs-tx-fee-status\">Fee: 0 SAITO</span> <button type=\"button\" class=\"saito-text-link rs-tx-fee-action\" data-action=\"set-fee\">change</button>";
  }
  return "<button type=\"button\" class=\"saito-text-link rs-tx-fee-action\" data-action=\"set-fee\">+ set transaction fee</button>";
}
function unlockTransactionPanelMarkup() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$inputs = _ref.inputs,
    inputs = _ref$inputs === void 0 ? [] : _ref$inputs,
    _ref$outputs = _ref.outputs,
    outputs = _ref$outputs === void 0 ? [] : _ref$outputs,
    _ref$selectedInputInd = _ref.selectedInputIndex,
    selectedInputIndex = _ref$selectedInputInd === void 0 ? null : _ref$selectedInputInd,
    _ref$mod = _ref.mod,
    mod = _ref$mod === void 0 ? null : _ref$mod;
  var inputsHtml = inputs.length ? inputs.map(function (row, index) {
    return inputRowMarkup(row, index, selectedInputIndex);
  }).join('') : "<p class=\"rs-tx-empty\">No inputs loaded.</p>";
  var outputsBlock = outputs.length > 0 ? "\n        <section class=\"rs-tx-section rs-tx-outputs\">\n          <h3 class=\"rs-tx-section-title\">Outputs</h3>\n          <div class=\"rs-tx-asset-list\">\n            ".concat(outputs.map(outputRowMarkup).join(''), "\n          </div>\n        </section>") : '';
  return "\n    <div class=\"rs-panel-ref rs-panel-ref-unlock-tx\">\n      <div class=\"rs-tx-panel\">\n        <section class=\"rs-tx-section rs-tx-inputs\">\n          <div class=\"rs-tx-section-head\">\n            <h3 class=\"rs-tx-section-title\">Inputs</h3>\n            ".concat(feeActionMarkup(mod), "\n          </div>\n          <div class=\"rs-tx-asset-list\">\n            ").concat(inputsHtml, "\n          </div>\n          ").concat(outputs.length > 0 ? '' : "<p class=\"rs-tx-hint\">click an input to create an output.</p>", "\n        </section>\n\n        ").concat(outputsBlock, "\n      </div>\n    </div>");
}
module.exports = {
  unlockInputRows: unlockInputRows,
  unlockOutputRows: unlockOutputRows,
  unlockTransactionPanelMarkup: unlockTransactionPanelMarkup
};